import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Mail, MessageCircle, MapPin, Star, ShieldCheck, Award, CheckCircle2, ArrowLeft, IndianRupee, Users } from "lucide-react";
import api from "../../lib/api";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { adminNav } from "../../components/shared/navConfig";
import { toast } from "sonner";

export default function AdminAllocation() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [apps, setApps] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (tId) => {
    setLoading(true);
    try {
      if (tId) {
        const [t, a] = await Promise.all([
          api.get(`/tasks/detail/${tId}`),
          api.get(`/admin/tasks/${tId}/applicants`),
        ]);
        setTask(t.data); setApps(a.data);
      } else {
        const { data } = await api.get("/admin/tasks?status=Approved");
        setTasks(data);
      }
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(taskId); }, [taskId]);

  const allocate = async (application_id, studentName) => {
    if (!window.confirm(`Allocate ${studentName} to this task? Other applicants will be rejected.`)) return;
    try {
      await api.post(`/admin/tasks/${taskId}/allocate`, { application_id });
      toast.success(`Allocated · ${studentName}`);
      load(taskId);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (application_id, status) => {
    try {
      await api.post(`/admin/applications/${application_id}/status`, { status });
      toast.success(`Marked ${status}`);
      load(taskId);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Task picker view
  if (!taskId) {
    return (
      <DashboardShell nav={adminNav} title="Allocate candidate" subtitle="Pick an approved task to review its applicants">
        {loading ? <div className="text-sm text-zinc-500">Loading…</div> : tasks.length === 0 ? (
          <div className="card-surface p-12 text-center text-sm text-zinc-500">
            <Users size={22} className="mx-auto mb-3 text-zinc-600"/>No approved tasks yet.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tasks.map(t => (
              <Link key={t.task_id} to={`/admin/allocation/${t.task_id}`} data-testid={`alloc-task-${t.task_id}`}
                    className="card-surface card-hover p-5 block">
                <div className="text-xs text-zinc-500 font-mono">{t.task_id}</div>
                <div className="font-display text-lg text-white mt-1">{t.title}</div>
                <div className="text-xs text-zinc-500 mt-1">{t.company_name}</div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
                  <div className="text-xs text-zinc-400 flex items-center gap-1"><IndianRupee size={11}/>{Number(t.budget || 0).toLocaleString()}</div>
                  <StatusBadge status={t.status}/>
                </div>
              </Link>
            ))}
          </div>
        )}
      </DashboardShell>
    );
  }

  const allocatedApp = apps.find(a => a.status === "Allocated");

  return (
    <DashboardShell nav={adminNav} title="Allocate candidate" subtitle={task ? `${task.title} · ${task.company_name}` : ""}>
      <Link to="/admin/allocation" data-testid="alloc-back" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-white mb-5 transition-colors duration-200"><ArrowLeft size={12}/> Back to tasks</Link>

      {task && (
        <div className="card-surface p-5 mb-6 flex items-center gap-4 flex-wrap">
          <div className="w-11 h-11 rounded-md flex items-center justify-center" style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)" }}>
            <Award size={18} className="text-blue-400"/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white">{task.title}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{apps.length} applicant{apps.length !== 1 && "s"} · Deadline {task.deadline || "—"} · ₹{Number(task.budget || 0).toLocaleString()}</div>
          </div>
          <StatusBadge status={task.status}/>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : apps.length === 0 ? (
        <div className="card-surface p-12 text-center text-sm text-zinc-500">
          <Users size={22} className="mx-auto mb-3 text-zinc-600"/>No applicants yet.
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          {apps.map(a => {
            const isAllocated = a.status === "Allocated";
            const isRejected = a.status === "Rejected";
            const p = a.profile || {};
            return (
              <div key={a.application_id} data-testid={`candidate-card-${a.application_id}`}
                   className={`card-surface p-5 relative overflow-hidden ${isRejected ? "opacity-60" : ""}`}>
                {a.match_score >= 90 && !isRejected && (
                  <div className="absolute top-3 right-3 text-[10px] font-mono px-2 py-0.5 rounded-full"
                       style={{ background: "rgba(16,185,129,0.10)", color: "#10B981", border: "1px solid rgba(16,185,129,0.35)" }}>Top pick</div>
                )}
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-blue-500/20 text-blue-300 flex items-center justify-center font-mono text-sm">
                    {(a.student_name || "?").slice(0,1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-white">{a.student_name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{p.college || "—"}</div>
                  </div>
                </div>

                <div className="mt-5 flex items-center gap-4">
                  <div className="relative w-16 h-16 rounded-full flex items-center justify-center"
                       style={{ background: `conic-gradient(#3B82F6 ${a.match_score * 3.6}deg, #1E1E22 0)` }}>
                    <div className="w-14 h-14 rounded-full bg-[#0A0A0C] flex flex-col items-center justify-center">
                      <span className="font-display text-lg text-white leading-none">{a.match_score}<span className="text-zinc-500 text-xs">%</span></span>
                      <span className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">Match</span>
                    </div>
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <Mini l="Rating" v={<span className="inline-flex items-center gap-1"><Star size={11} className="text-amber-400 fill-amber-400"/>4.6</span>}/>
                    <Mini l="Availability" v={a.availability || "—"}/>
                    <Mini l="Experience" v={a.experience || "—"}/>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t space-y-2" style={{ borderColor: "var(--border)" }}>
                  <Info l="Distance" v={<span className="inline-flex items-center gap-1"><MapPin size={10}/>{p.service_city || p.city || "—"}</span>}/>
                  <Info l="Skills" v={(a.relevant_skills || []).slice(0, 3).join(" · ") || "—"}/>
                  <Info l="Portfolio" v={a.portfolio_url ? <a target="_blank" rel="noreferrer" href={a.portfolio_url} className="text-blue-400 hover:text-blue-300">Open link</a> : `${(a.portfolio_file_ids || []).length} files`}/>
                </div>

                {a.proposal && (
                  <div className="mt-3 p-3 rounded-md text-xs text-zinc-300 leading-relaxed" style={{ background: "var(--surface-2)" }}>
                    "{a.proposal}"
                  </div>
                )}

                <div className="mt-4 flex items-center gap-2">
                  <StatusBadge status={a.status === "Submitted" ? "Applied" : a.status}/>
                </div>

                {isAllocated ? (
                  <div className="mt-5 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-emerald-400"><CheckCircle2 size={13}/> Allocated</div>
                    <div className="flex gap-2">
                      <a data-testid={`send-email-${a.application_id}`} href={`mailto:${a.student_email}?subject=Task%20${task?.task_id}%20-%20Allocated&body=Congrats%20${encodeURIComponent(a.student_name || "")},%20you%20have%20been%20allocated%20to%20${encodeURIComponent(task?.title || "")}.`}
                         className="btn-ghost flex-1 py-2 rounded-md text-xs inline-flex items-center justify-center gap-1.5">
                        <Mail size={12}/> Email
                      </a>
                      <a data-testid={`send-whatsapp-${a.application_id}`}
                         target="_blank" rel="noreferrer"
                         href={`https://wa.me/${(p.mobile || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi ${a.student_name || ""}, you have been allocated to ${task?.title || ""} (${task?.task_id}) via Assisto4U. Open the workroom to check in.`)}`}
                         className="btn-ghost flex-1 py-2 rounded-md text-xs inline-flex items-center justify-center gap-1.5">
                        <MessageCircle size={12}/> WhatsApp
                      </a>
                    </div>
                  </div>
                ) : allocatedApp ? (
                  <div className="mt-5 text-[11px] text-zinc-500 text-center">Another candidate is allocated to this task.</div>
                ) : (
                  <div className="mt-5 space-y-2">
                    <button data-testid={`allocate-btn-${a.application_id}`} onClick={() => allocate(a.application_id, a.student_name)}
                            className="btn-primary w-full py-2 rounded-md text-xs font-medium uppercase tracking-widest">
                      Allocate student
                    </button>
                    <div className="grid grid-cols-2 gap-2">
                      <button data-testid={`shortlist-btn-${a.application_id}`} onClick={() => setStatus(a.application_id, a.status === "Shortlisted" ? "Under Review" : "Shortlisted")}
                              className="btn-ghost py-1.5 rounded-md text-[11px]">
                        {a.status === "Shortlisted" ? "Unshortlist" : "Shortlist"}
                      </button>
                      <button data-testid={`reject-btn-${a.application_id}`} onClick={() => setStatus(a.application_id, "Rejected")}
                              className="btn-ghost py-1.5 rounded-md text-[11px] hover:text-rose-400">
                        Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </DashboardShell>
  );
}

const Info = ({ l, v }) => (
  <div className="flex items-center justify-between text-xs">
    <span className="text-zinc-500">{l}</span>
    <span className="text-zinc-300 truncate max-w-[160px]">{v}</span>
  </div>
);
const Mini = ({ l, v }) => (
  <div className="flex items-center justify-between text-[11px]">
    <span className="text-zinc-500 uppercase tracking-widest text-[9px]">{l}</span>
    <span className="text-zinc-200 truncate max-w-[110px]">{v}</span>
  </div>
);
